import { useCallback, ReactNode, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
    Dialog as HeadlessDialog,
    DialogPanel as HeadlessDialogPanel,
    DialogBackdrop as HeadlessDialogBackdrop,
} from '@headlessui/react'
import classNames from 'classnames'

import { DialogProps } from './types'
import { DialogContext } from './Context'

type DialogContainerProps = DialogProps & {
    onClose: (value?: any) => void
    children: ReactNode
    onDestroyDialog: () => void
}

// take care here the code is a little bit confusing when closing the dialog, but what we do is:
// 1 - we set isOpen to false then the headless lib will handle some stuff
//     and allow we interact with the whole page again (this step is important)
// 2 - we call the onClose event
// 3 - we call onDestroyDialog the dialog is destroyed

export const DialogModal = observer(
    ({
        width = 'md',
        fullHeight,
        noPadding,
        noBorder,
        noDark,
        allowCloseByClickEscape,
        responsive,
        light,
        onClose,
        children,
        onDestroyDialog,
    }: DialogContainerProps) => {
        const [isOpen, setIsOpen] = useState(true)

        const handleClose = useCallback(
            (value?: any) => {
                setIsOpen(false)
                onClose(value)
                onDestroyDialog()
            },
            [onClose, onDestroyDialog]
        )

        const handleDialogClose = useCallback(() => {
            if (allowCloseByClickEscape) {
                handleClose()
            }
        }, [allowCloseByClickEscape, handleClose])

        const contextValue = useMemo(
            () => ({
                width,
                fullHeight,
                noPadding,
                close: handleClose,
            }),
            [width, fullHeight, noPadding, handleClose]
        )

        return (
            <HeadlessDialog as="div" className="relative z-20" onClose={handleDialogClose} open={isOpen}>
                <HeadlessDialogBackdrop className="fixed inset-0 bg-black/20 dark:bg-black/60" />
                <div className="fixed inset-0 overflow-y-auto">
                    <div
                        className={classNames('flex min-h-full items-center justify-center text-center', {
                            'p-4': !responsive,
                            'md:p-4': !!responsive,
                            'sm:p-0': !!responsive,
                        })}
                    >
                        <HeadlessDialogPanel
                            className={classNames(
                                'w-full transform rounded-xl text-left align-middle shadow-xl transition-all',
                                'shrink-1 flex max-h-[calc(100vh_-_64px)] flex-col overflow-y-auto overflow-x-hidden',
                                {
                                    'max-w-xs': width == 'sm',
                                    'max-w-[600px]': width === 'md',
                                    'max-w-4xl': width === 'lg',
                                    'max-w-6xl': width === 'xl',
                                    // -- large has no restriction as it takes the whole screen
                                    'h-[calc(100vh-64px)]': fullHeight,
                                    'p-6': !noPadding,
                                    border: !noBorder,
                                    'border-neutral-300 bg-white dark:border-neutral-700 dark:bg-neutral-800':
                                        noDark && !light,
                                    'border-neutral-400 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900':
                                        !noDark && !light,
                                    'border-neutral-400 bg-neutral-100': !!light,
                                }
                            )}
                        >
                            <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>
                        </HeadlessDialogPanel>
                    </div>
                </div>
            </HeadlessDialog>
        )
    }
)
